import { typedApi } from '../../api/typed';
import { useResource } from '../../hooks/useResource';
import { Users, Phone, Target, CreditCard, AlertTriangle, Clock } from 'lucide-react';

const SOURCE_ICON = { call: Phone, delegation: Target, payment: CreditCard };
const SOURCE_COLOR = { call: 'var(--pd-focus)', delegation: 'var(--pd-warning)', payment: 'var(--pd-success)' };

export default function StakeholderHeat() {
  const { data, loading } = useResource(
    'stakeholder-heat',
    async ({ signal }) => await typedApi.get('/api/stakeholder-heat', { signal }),
    { initialData: { contacts: [] } },
  );
  const contacts = data?.contacts || [];

  if (loading) return <div className="px-4 py-3 space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 rounded-xl skeleton-shimmer" />)}</div>;
  if (contacts.length === 0) return (
    <div className="px-4 py-6 text-center">
      <Users className="w-5 h-5 mx-auto mb-2" style={{ color: 'var(--pd-border)' }} />
      <p className="text-[11px]" style={{ color: 'var(--pd-subtle)' }}>No stakeholder pressure detected</p>
    </div>
  );

  const maxScore = Math.max(...contacts.map(c => c.score), 1);

  return (
    <div data-testid="stakeholder-heat-index" className="space-y-1">
      {contacts.slice(0, 5).map((contact, idx) => {
        const heatPct = Math.round((contact.score / maxScore) * 100);
        const isHot = heatPct >= 70;
        const isWarm = heatPct >= 40;

        return (
          <div key={`${contact.name}-${idx}`} data-testid={`stakeholder-${idx}`}
            className="px-4 py-2.5 group"
            style={{ borderBottom: '1px solid var(--pd-border-subtle)' }}>
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                style={{
                  background: isHot ? 'rgba(180,35,58,0.1)' : isWarm ? 'rgba(180,83,9,0.08)' : 'var(--pd-panel-2)',
                  color: isHot ? 'var(--pd-crimson)' : isWarm ? 'var(--pd-warning)' : 'var(--pd-muted)',
                }}>
                {contact.name.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="text-[12px] font-semibold truncate" style={{ color: 'var(--pd-text)' }}>
                    {contact.name}
                  </span>
                  {contact.businesses.map((biz, bi) => (
                    <span key={`biz-${bi}-${biz}`} className="text-[8px] px-1 py-[1px] rounded-full font-semibold"
                      style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-subtle)' }}>
                      {biz}
                    </span>
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  {contact.sources.map((src, si) => {
                    const SrcIcon = SOURCE_ICON[src] || Users;
                    return (
                      <span key={`src-${si}-${src}`} className="text-[9px] flex items-center gap-0.5"
                        style={{ color: SOURCE_COLOR[src] || 'var(--pd-subtle)' }}>
                        <SrcIcon className="w-2.5 h-2.5" />{src}
                      </span>
                    );
                  })}
                  <span className="text-[9px] tabular-nums" style={{ color: 'var(--pd-muted)' }}>
                    {contact.pending} pending
                  </span>
                  {contact.overdue > 0 && (
                    <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'var(--pd-crimson)' }}>
                      <AlertTriangle className="w-2.5 h-2.5" />{contact.overdue} overdue
                    </span>
                  )}
                  {contact.lagDays > 0 && (
                    <span className="text-[9px] flex items-center gap-0.5" style={{ color: 'var(--pd-warning)' }}>
                      <Clock className="w-2.5 h-2.5" />{contact.lagDays}d lag
                    </span>
                  )}
                </div>
              </div>
              <div className="w-12 text-right flex-shrink-0">
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--pd-panel-2)' }}>
                  <div className="h-full rounded-full" style={{
                    width: `${heatPct}%`,
                    background: isHot ? 'var(--pd-crimson)' : isWarm ? 'var(--pd-warning)' : 'var(--pd-focus)',
                  }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
